import {
    Box, Button, Card, CardContent, Chip, CircularProgress, Container, Dialog, DialogActions, DialogContent,
    DialogTitle, Fab, TextField, Typography
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import User from '../api/user';
import { useDispatch } from "react-redux";
import { show } from '../reducers/notificationSlice';
import { Add, Delete, Person, Edit } from "@mui/icons-material";
import { Grid as GridLoader } from 'react-loader-spinner';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { getDashboardUserId } from '../utils/dashboardAuth';

export default function ProfilePage() {

    const dispatch = useDispatch();
    const [profile, setProfile] = useState({});
    const [profileLoading, setProfileLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [adminList, setAdminList] = useState(null);
    const [profileImage, setProfileImage] = useState({});
    const ref = useRef();
    const [formData, setFormData] = useState({
        name: "",
        email: "",
    });

    function addFormData(key, value) {
        setFormData((_d) => ({
            ..._d,
            [key]: value
        }));
    }
    useEffect(() => {
        const uid = getDashboardUserId();
        if (!uid) {
            setProfileLoading(false);
            return;
        }
        User.getUserDetails(uid)
            .then(({ data }) => {
                setProfile(data && typeof data === "object" ? data : {});
            })
            .catch((err) => dispatch(show({ message: err?.response?.data?.message, open: true, severity: "error" })))
            .finally(() => setProfileLoading(false));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Container maxWidth="lg" sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: '90vw' }}>
            <Card className="flex md:flex-row md:justify-between items-center p-2 md:p-5 flex-col max-w-full">
                {profileLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4, width: "100%" }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <Box className="relative p-2">
                            {profile.profileImageURL || profileImage.profileImageURL ?
                                <img src={profileImage.profileImageURL || profile.profileImageURL}
                                    alt="" className="w-24 rounded-full" /> :
                                <Box className="border rounded-full w-24 h-24 flex justify-center items-center"><Person sx={{ fontSize: 50 }} /></Box>
                            }
                        </Box>
                        <CardContent className="flex flex-col gap-1 md:items-start items-center">
                            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.5 }}>
                                Signed in as administrator
                            </Typography>
                            <Typography variant="h5">
                                {profile.name || "—"}
                            </Typography>
                            <Typography color="text.secondary">
                                {profile.email || "—"}
                            </Typography>
                            {profile.id != null && (
                                <Typography variant="body2" color="text.secondary">
                                    User ID: {profile.id}
                                </Typography>
                            )}
                            <Chip
                                size="small"
                                color="primary"
                                variant="outlined"
                                label={profile.profileTypeID === 4 ? "Administrator" : "User"}
                                sx={{ alignSelf: { xs: "center", md: "flex-start" }, mt: 0.5 }}
                            />
                        </CardContent>
                    </>
                )}
            </Card>
            <ListAdmins {...{ adminList, setAdminList, setFormData, setOpen }} />
            <input type="file" className="hidden" ref={ref} onChange={(e) => {
                const fr = new FileReader();
                try {
                    fr.onload = () => {
                        // Image upload functionality - convert to URL and update profile image
                        const imageUrl = fr.result;
                        setProfileImage({ profileImageURL: imageUrl });
                        ref.current.value = "";
                    }
                    fr.readAsDataURL(e.target.files[0]);
                } catch (e) {
                    dispatch(show({ message: "Error while uploading image", severity: "error", open: true }));
                }
            }} />
            {/* Image cropping functionality can be implemented here if needed */}
            <AdminDialog
                open={open}
                setOpen={setOpen}
                formData={formData}
                addFormData={addFormData}
                setFormData={setFormData}
                setAdminList={setAdminList}
            />
        </Container>
    )
}

function AdminDialog({ open, setOpen, formData, addFormData, setFormData, setAdminList }) {
    const dispatch = useDispatch();

    return (
        <Dialog open={open} onClose={() => {
            setFormData(() => ({ name: "", email: "" }));
            setOpen(false);
        }}>
            <DialogTitle>
                <Typography className="font-bold text-center" variant="h4">{formData.id ? "Edit Admin" : "Add Admin"}</Typography>
            </DialogTitle>
            <DialogContent>
                <Box className="flex flex-col gap-8 w-[400px] p-6 max-w-full">
                    <TextField label="Name" value={formData.name} onChange={(e) => addFormData('name', e.target.value)} />
                    <TextField label="Email" value={formData.email} onChange={(e) => addFormData('email', e.target.value)} />
                </Box>
            </DialogContent>
            <DialogActions sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button variant="contained" size="large" onClick={() => {
                    const action = formData.id ? User.updateAdmin(formData.id, formData) : User.addAdmin(formData);
                    action
                        .then(() => {
                            dispatch(show({ message: `Admin Profile ${formData.id ? "updated" : "added"} successfully`, open: true, severity: "success" }));
                            setFormData(() => ({ name: "", email: "" }));
                            setOpen(false);
                            return User.getAdminList();
                        })
                        .then(({ data }) => {
                            setAdminList(Array.isArray(data) ? data : []);
                        })
                        .catch((err) => dispatch(show({ message: err?.response?.data?.message, open: true, severity: "error" })));
                }}>{formData.id ? "Update" : "Add"}</Button>
            </DialogActions>
        </Dialog>
    )
}

function ListAdmins({ adminList, setAdminList, setFormData, setOpen }) {

    const dispatch = useDispatch();

    useEffect(() => {
        User.getAdminList()
            .then(({ data }) => {
                const list = Array.isArray(data) ? data : [];
                setAdminList(list);
            }).catch((err) => dispatch(show({ message: err?.response?.data?.message, open: true, severity: "error" })));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Box>
            <Box className="flex justify-between items-center p-4">
                <Typography variant="h4">System Admins</Typography>
                <Button variant="contained" startIcon={<Add />} onClick={() => {
                    setFormData({ name: "", email: "" });
                    setOpen(true);
                }}>
                    Add Admin
                </Button>
            </Box>
            {
                <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 650, maxWidth: '100%' }} aria-label="admin table">
                        <TableHead>
                            <TableRow sx={{ background: 'black' }}>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Avatar</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Name</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Email</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(adminList) !== null ? (adminList?.length > 0 ? adminList?.map((row) => (
                                <TableRow
                                    key={row.id ?? row.email}
                                    sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover td': { background: 'rgba(0,0,0,0.7)', color: 'white' } }}
                                >
                                    <TableCell scope="row">
                                        {row.profileImageURL ?
                                            <img src={row.profileImageURL}
                                                alt="avatar" className="rounded-full border w-12 h-12" /> :
                                            <Box className="border rounded-full w-12 h-12 flex justify-center items-center"><Person sx={{ fontSize: 20 }} /></Box>}
                                    </TableCell>
                                    <TableCell>{row.name}</TableCell>
                                    <TableCell>{row.email}</TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Fab color="primary" size="small" onClick={() => {
                                                setFormData({ id: row.id, name: row.name, email: row.email });
                                                setOpen(true);
                                            }}><Edit /></Fab>
                                            <Fab color="error" size="small" onClick={() => {
                                                User.deleteAdmin(row.id)
                                                    .then(() => {
                                                        setAdminList((adminList) => (adminList.filter((ele) => ele.id !== row.id)))
                                                        dispatch(show({ message: "Admin removed successfully", open: true, severity: "success" }));
                                                    })
                                                    .catch((err) => dispatch(show({ message: err?.response?.data?.message, open: true, severity: "error" })));
                                            }}><Delete /></Fab>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            )) : <Typography className="flex justify-center text-red-400" variant="h5">No Admins found</Typography>) :
                                <div className="flex p-10 justify-center"><GridLoader /></div>}
                        </TableBody>
                    </Table>
                </TableContainer>
            }
        </Box>
    )
}
